import { useImmer } from "use-immer";

import { IgTextField } from "@intelligems/ig-theme";
import { ExpressionNodeQueryType } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";

interface RegexpValueProps {
  value: string;
  onChange: (
    value: any,
    changeKey: Exclude<keyof ExpressionNodeQueryType, "type">
  ) => void;
}

export const RegexpValue = ({ value, onChange }: RegexpValueProps) => {
  const theme = useTheme();
  const [input, setInput] = useImmer<string>(value);
  const [error, setError] = useImmer<boolean>(false);
  const [helperText, setHelperText] = useImmer<string | null>(null);

  const validateAndChange = (value: string) => {
    const { hasError, message } = validate(value);
    setInput(value);
    setError(hasError);
    setHelperText(message);
    if (hasError) {
      // ensure that this experience is not saveable.
      onChange("", "value");
    } else {
      onChange(value, "value");
    }
  };

  return (
    <Grid item flexGrow={1}>
      <IgTextField
        error={error}
        helperText={helperText}
        fullWidth={true}
        value={input}
        onChange={(e) => validateAndChange(e.target.value)}
        sx={{
          ...(!error && helperText
            ? {
                "& p": {
                  color: theme.palette.text.tonal.warning,
                },
              }
            : undefined),
        }}
      />
    </Grid>
  );
};

// Basically anything that wasn't supported until after 2018
const partialFeatureList: {
  regex: RegExp;
  error?: boolean;
  incompatibleBrowsers: string[];
}[] = [
  {
    regex: /\(\?[ims]+(-[ims]+)?:/,
    error: true,
    // https://caniuse.com/mdn-javascript_regular_expressions_modifier
    incompatibleBrowsers: ["Chrome", "Edge", "Safari", "Firefox"],
  },
  {
    regex: /\(\?<[\w$]*>|\\k<[\w$]*>/,
    error: true,
    // https://caniuse.com/mdn-javascript_regular_expressions_named_capturing_group
    // https://caniuse.com/mdn-javascript_regular_expressions_named_backreference
    incompatibleBrowsers: ["Chrome", "Edge", "Safari", "Firefox"],
  },
  {
    regex: /\(\?<=|\(\?<!/,
    error: true,
    // https://caniuse.com/js-regexp-lookbehind
    incompatibleBrowsers: ["Safari", "Firefox"],
  },
  {
    regex: /\\[pP]\{[\w$=]*\}/,
    error: true,
    // https://caniuse.com/mdn-javascript_regular_expressions_unicode_character_class_escape
    incompatibleBrowsers: ["Firefox"],
  },
];

const validate = (value: string) => {
  const validationErrors = partialFeatureList.filter((feature) => {
    return feature.regex.test(value);
  });

  const browsers = validationErrors.flatMap(
    (error) => error.incompatibleBrowsers
  );
  let hasError = validationErrors.some((error) => error.error);

  let message: string | null = null;
  if (browsers.length > 1) {
    message = `Incompatible with${hasError ? "" : " some versions of"} multiple browsers`;
  } else if (browsers.length === 1) {
    message = `Incompatible with${hasError ? "" : " some versions of"} ${browsers[0]}`;
  }

  if (!hasError) {
    try {
      new RegExp(value);
    } catch (_) {
      hasError = true;
      message = "Invalid regular expression";
    }
  }

  return {
    hasError,
    message: !hasError && message ? `Warning: ${message}` : message,
  };
};
